// src/redux/sagas/gameSaga.ts
import {call, put, Effect, delay, takeLatest, takeEvery} from 'redux-saga/effects';
import {
    setAuthorization,
    setAuthTokens,
    setUser,
    setAppLanguage,
    setTickets,
    setTokens,
    setFriends,
    setAddress,
    setSecondsToClaim,
    setLeaderboard,
    setMetadata,
    setActiveState,
} from '../slices/gameSlice';
import {PayloadAction} from "@reduxjs/toolkit";
import {apiCaller, waitForAuthorization} from "./apiCaller";
import telegramWebAppAPI from "../../services/telegram";
import {User} from "../types";
import {i18n} from "@lingui/core";


function* handleAuthorize(action: PayloadAction<string>): Generator<any, void, any> {
    try {
        const tokens = yield call(apiCaller, { apiFuncName: 'authorize', args: action.payload});
        yield put(setAuthTokens(tokens));
        yield put(setAuthorization(true));

        yield put({ type: 'game/fetchUser' });
        yield delay(400);
        yield put({type: 'game/fetchLeaderboard'});
        yield delay(400);
        yield put({type: 'game/fetchMetadata'});
    } catch (error) {
        console.error(error);
        yield put(setAuthorization(false));
    }
}

export function *fetchUpdates(): Generator<any, void, any> {
    try {
        const {user} = yield call(apiCaller, { apiFuncName: 'fetchProfile', args: {}});
        yield call(applyUpdates, {user})
    } catch (e) {
        console.error(e);
    }
}

function* handleFetchUser(): Generator<any, void, any> {
    try {
        const {user} = yield call(apiCaller, { apiFuncName: 'fetchProfile', args: {}});

        yield put(setUser(user));

        // set default user language
        if(user.app_language)  {
            i18n.activate(user.app_language);
        }
        // yield put(setFriends(friends));
        yield put({type: 'game/setIsLoaded', payload: true});
    } catch (error) {
        console.error(error);
    }
}

function* blockExit(): Generator<any, void, any> {
    const confirm = yield call(telegramWebAppAPI.isClosingConfirmationEnabled);
    if(!confirm)
        yield call(telegramWebAppAPI.enableClosingConfirmation);
}


interface ApplyUpdatesArgs {
    user: User
}

// apply update to user in store
function *applyUpdates({user}: ApplyUpdatesArgs): Generator<any, void, any> {
    yield put(setAddress(user.airdrop_address));
    yield put(setTickets(user.tickets));
    yield put(setTokens(user.tokens));
    yield put(setFriends(user.friends));
    yield put(setSecondsToClaim(user.seconds_to_claim));
    yield put(setActiveState(user.active_state));
    yield put(setAppLanguage(user.app_language || 'en'));
}

export function* every5Minutes() {
    try {
        yield delay(60000 * 5);
        yield put({type: 'game/fetchLeaderboard'});
        yield put({type: 'game/fetchUpdates'});
        yield put({ type: "game/every5Minutes" })
    } catch (e) {
        console.error(e);
    }
}

function* handleMetadata(): Generator<any, void, any> {
    // Wait until the authorization is complete
    yield waitForAuthorization();

    try {
        const leaders = yield call(apiCaller, { apiFuncName: 'fetchLeaderboard', args: {}});
        yield put(setLeaderboard(leaders));
    } catch (error) {
        console.error(error);
    }
}

function* handleLeaderboard(): Generator<any, void, any> {
    // Wait until the authorization is complete
    yield waitForAuthorization();

    try {
        const info = yield call(apiCaller, { apiFuncName: 'fetchMetadata', args: {}});
        yield put(setMetadata(info));
    } catch (error) {
        console.error(error);
    }
}

function *handleUpdateState(action: PayloadAction<number>): Generator<any, void, any> {
    // Wait until the authorization is complete
    yield waitForAuthorization();

    try {
        yield call(apiCaller, { apiFuncName: 'updateState', args: action.payload});
        yield call(fetchUpdates);
    } catch (error) {
        console.error(error);
    }
}


function *setLanguageCode(action: PayloadAction<string>): Generator<any, void, any> {
    const {user} = yield call(apiCaller, { apiFuncName: 'setLanguage', args: {language: action.payload}});
    yield call(applyUpdates, {user})
}

function* handleClaimTicket(action: PayloadAction<number>): Generator<any, void, any> {
    try {
        yield call(apiCaller, { apiFuncName: 'claimTicket', args: action.payload});
        yield call(fetchUpdates);
    } catch (error) {
        console.error(error);
    }
}

function *handleUpdateAddress(action: PayloadAction<string>): Generator<any, void, any> {
    const {user} = yield call(apiCaller, { apiFuncName: 'setAddress', args: {address: action.payload}});
    yield call(applyUpdates, {user})
}

export default function* gameSaga(): Generator<Effect, void, unknown> {
    yield takeLatest('game/authorize', handleAuthorize);
    yield takeLatest('game/fetchUser', handleFetchUser);
    yield takeEvery('game/setLanguageCode', setLanguageCode);
    yield takeLatest('game/every5Minutes', every5Minutes)
    yield takeEvery('game/claimTicket', handleClaimTicket);
    yield takeEvery('game/fetchLeaderboard', handleLeaderboard);
    yield takeEvery('game/fetchMetadata', handleMetadata);
    yield takeEvery('game/fetchUpdates', fetchUpdates);
    yield takeEvery('game/updateState', handleUpdateState);
    yield takeEvery('game/updateAddress', handleUpdateAddress);
}
